import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/utils/cn';
import { formatDateForDisplay } from '@/utils/format';
import {
  metaAdsApi,
  type MetaAdSetCreateStatus,
  type MetaBillingEvent,
  type MetaCampaignObjective,
  type MetaOptimizationGoal,
  type MetaPromotedObject,
  type MetaTargetingSpec,
} from '@/services/api/metaAds';
import type { MetaBudgetMode } from './MetaBudgetStep';
import { OBJECTIVES_REQUIRING_PROMOTED_OBJECT } from '../constants/metaAdSetMatrix';

interface AdSetReviewStepProps {
  parentObjective: MetaCampaignObjective;
  parentCampaignName: string;
  /** Parent campaign's bid_strategy — drives the bid_amount label/format.
   *  `LOWEST_COST_WITH_MIN_ROAS` reads as a ROAS multiplier (e.g. 1.5×),
   *  the constrained money strategies (BID_CAP / COST_CAP) read as USD. */
  parentBidStrategy: string | null;
  adSetName: string;
  optimizationGoal: MetaOptimizationGoal;
  billingEvent: MetaBillingEvent;
  status: MetaAdSetCreateStatus;
  budgetMode: MetaBudgetMode;
  budgetAmount: string;
  bidAmount: string;
  showBidAmount: boolean;
  /** Under CBO the parent campaign carries the budget — the Review's
   *  budget card surfaces "Inherited from campaign" instead of $ amount
   *  and the Edit button is hidden (no Budget step to jump to). Bid
   *  amount is NOT suppressed by CBO: Meta requires `bid_amount` per
   *  ad set whenever the parent campaign's bid_strategy is constrained
   *  (BID_CAP / COST_CAP / MIN_ROAS), independent of CBO. The bid row
   *  renders whenever `showBidAmount` is true. */
  parentIsCbo: boolean;
  startDate: string;
  endDate: string;
  hasEndDate: boolean;
  targeting: MetaTargetingSpec;
  promotedObject: MetaPromotedObject | null;
  /** Ad account ID — required to scope the Page + lead-form lookup
   *  queries so we can render human-readable names in the Promoted
   *  Object summary instead of raw IDs. Same `act_<digits>` form
   *  used elsewhere in the wizard. */
  adAccountId: string;
  /** Jump to a specific step. Same UX pattern as the campaign wizard's
   *  ReviewStep — clicking a section's Edit button rewinds to that step. */
  onEditStep: (step: number) => void;
}

const OBJECTIVE_LABELS: Record<MetaCampaignObjective, string> = {
  OUTCOME_SALES: 'Sales',
  OUTCOME_TRAFFIC: 'Traffic',
  OUTCOME_AWARENESS: 'Awareness',
  OUTCOME_ENGAGEMENT: 'Engagement',
  OUTCOME_LEADS: 'Leads',
  OUTCOME_APP_PROMOTION: 'App promotion',
};

const OPTIMIZATION_GOAL_LABELS: Record<MetaOptimizationGoal, string> = {
  REACH: 'Reach',
  IMPRESSIONS: 'Impressions',
  AD_RECALL_LIFT: 'Ad recall lift',
  LINK_CLICKS: 'Link clicks',
  LANDING_PAGE_VIEWS: 'Landing page views',
  POST_ENGAGEMENT: 'Post engagement',
  PAGE_LIKES: 'Page likes',
  EVENT_RESPONSES: 'Event responses',
  THRUPLAY: 'ThruPlay (video)',
  VIDEO_VIEWS: 'Video views',
  OFFSITE_CONVERSIONS: 'Conversions (pixel)',
  VALUE: 'Conversion value',
  LEAD_GENERATION: 'Lead generation',
  QUALITY_LEAD: 'Quality lead',
  APP_INSTALLS: 'App installs',
  CONVERSATIONS: 'Messenger conversations',
};

const BILLING_EVENT_LABELS: Record<MetaBillingEvent, string> = {
  IMPRESSIONS: 'Per impression',
  LINK_CLICKS: 'Per link click',
  THRUPLAY: 'Per ThruPlay',
  POST_ENGAGEMENT: 'Per engagement',
  PAGE_LIKES: 'Per page like',
  APP_INSTALLS: 'Per app install',
};

const STATUS_LABELS: Record<MetaAdSetCreateStatus, string> = {
  PAUSED: 'Paused',
  ACTIVE: 'Active',
};

const summariseTargeting = (t: MetaTargetingSpec): string => {
  const parts: string[] = [];
  parts.push(`${t.geo_locations.countries.join(', ')} (countries)`);
  parts.push(`Ages ${t.age_min}–${t.age_max}`);
  if (t.genders.length === 2) {
    parts.push('All genders');
  } else if (t.genders.length === 1) {
    parts.push(t.genders[0] === 'MALE' ? 'Men' : 'Women');
  }
  if (t.interests.length > 0) {
    parts.push(
      `${t.interests.length} interest${t.interests.length === 1 ? '' : 's'}`,
    );
  }
  // Empty publisher_platforms is Meta's wire convention for Advantage+
  // Placements (the serializer omits empty placement keys, which Meta
  // reads as automatic). Defaulting to Advantage+ is intentional in
  // `CreateAdSetWizard.tsx defaultTargeting()`, but if we labeled that
  // state as plain "Advantage+ Placements" on the Review screen the
  // user would think they opted in — when really the wizard chose it
  // for them. Append "(default)" so they can tell the difference.
  if (t.publisher_platforms.length > 0 && !t.advantage_placements) {
    parts.push(`Manual placements (${t.publisher_platforms.join(', ')})`);
  } else if (t.advantage_placements && t.publisher_platforms.length === 0) {
    parts.push('Advantage+ Placements (default)');
  } else {
    parts.push('Advantage+ Placements');
  }
  if (t.device_platforms.length > 0 && t.device_platforms.length < 2) {
    parts.push(t.device_platforms[0] === 'mobile' ? 'Mobile only' : 'Desktop only');
  }
  if (t.locales.length > 0) {
    parts.push(`${t.locales.length} language${t.locales.length === 1 ? '' : 's'}`);
  }
  if (t.advantage_audience) {
    parts.push('Advantage Audience on');
  }
  return parts.join(' · ');
};

const summarisePromotedObject = (
  po: MetaPromotedObject,
  parentObjective: MetaCampaignObjective,
  pageName?: string,
): string => {
  if (parentObjective === 'OUTCOME_SALES') {
    return `Pixel ${po.pixel_id ?? '—'} / ${po.custom_event_type ?? '—'}`;
  }
  if (parentObjective === 'OUTCOME_LEADS') {
    // Prefer name if the lookup query resolved; fall back to ID.
    // ID fallback keeps the summary informative if (a) the query is
    // still loading or (b) the granted Page was revoked between the
    // picker step and this Review step (rare but possible). The lead
    // form itself isn't summarised here — Meta's promoted_object for
    // LEADS only takes page_id; the form_id is selected on the ad
    // creative wizard, not here.
    return pageName ?? po.page_id ?? '—';
  }
  if (parentObjective === 'OUTCOME_APP_PROMOTION') {
    return `App ${po.application_id ?? '—'} (${po.object_store_url ?? '—'})`;
  }
  return '—';
};

/**
 * Step 6 — review every section before submit. Same structural shape as
 * `MetaReviewStep` (per-domain bordered cards + Edit buttons +
 * "Before You Create" yellow notice), Meta-ad-set field set.
 */
export const AdSetReviewStep: React.FC<AdSetReviewStepProps> = ({
  parentObjective,
  parentCampaignName,
  parentBidStrategy,
  adSetName,
  optimizationGoal,
  billingEvent,
  status,
  budgetMode,
  budgetAmount,
  bidAmount,
  showBidAmount,
  parentIsCbo,
  startDate,
  endDate,
  hasEndDate,
  targeting,
  promotedObject,
  adAccountId,
  onEditStep,
}) => {
  // Look up human-readable Page name for the Promoted Object summary
  // (LEADS only). Same query key + staleTime as AdSetPromotedObjectStep
  // so React Query serves a cache-hit without re-fetch when the user
  // advances from the picker to Review.
  const isLeads = parentObjective === 'OUTCOME_LEADS';
  const lookupPageId = isLeads ? (promotedObject?.page_id ?? '') : '';
  const { data: pages = [] } = useQuery({
    queryKey: ['meta-pages', adAccountId],
    queryFn: () => metaAdsApi.listPages(adAccountId),
    staleTime: 5 * 60 * 1000,
    enabled: isLeads && Boolean(lookupPageId),
  });
  const pageName = pages.find((p) => p.id === lookupPageId)?.name;

  const budgetValue = parseFloat(budgetAmount) || 0;
  const budgetDisplay =
    budgetValue > 0
      ? `$${budgetValue.toFixed(2)} ${budgetMode === 'daily' ? '/ day' : 'lifetime'}`
      : 'Not set';
  const budgetDetail =
    budgetMode === 'daily' && budgetValue > 0
      ? `Estimated 30 days: $${(budgetValue * 30).toFixed(2)}`
      : undefined;
  const bidValue = parseFloat(bidAmount) || 0;
  // MIN_ROAS interprets bid_amount as a decimal ROAS multiplier (e.g.
  // 1.5×); the other constrained strategies (BID_CAP / COST_CAP) read
  // it as USD. Format accordingly so users review the value with the
  // unit they actually typed.
  const isMinRoas = parentBidStrategy === 'LOWEST_COST_WITH_MIN_ROAS';
  const bidLabel = isMinRoas ? 'Minimum ROAS goal' : 'Bid amount';
  const bidDisplay = bidValue > 0
    ? (isMinRoas ? `${bidValue}× ROAS` : `$${bidValue.toFixed(2)}`)
    : 'Not set';

  const endExpected = budgetMode === 'lifetime' || hasEndDate;
  const requiresPromoted = OBJECTIVES_REQUIRING_PROMOTED_OBJECT.has(parentObjective);

  const basicsItems = [
    { label: 'Ad set name', value: adSetName || 'Not set' },
    { label: 'Optimization goal', value: OPTIMIZATION_GOAL_LABELS[optimizationGoal] },
    { label: 'Billing event', value: BILLING_EVENT_LABELS[billingEvent] },
    { label: 'Initial status', value: STATUS_LABELS[status] },
  ];

  const budgetItems: Array<{ label: string; value: string; detail?: string }> = [
    parentIsCbo
      ? { label: 'Budget', value: 'Inherited from campaign (CBO)' }
      : { label: 'Budget', value: budgetDisplay, detail: budgetDetail },
  ];
  if (showBidAmount) {
    budgetItems.push({ label: bidLabel, value: bidDisplay });
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Review Your Ad Set
        </h3>
        <p className="text-sm text-gray-600">
          Review settings before creating. Click any section to edit.
        </p>
      </div>

      {/* Parent context — read-only. Changing the parent campaign mid-flow
          would invalidate every later step, so the user closes the wizard
          and reopens from a different campaign if needed. */}
      <ReviewSection
        title="Parent Campaign"
        items={[
          { label: 'Campaign', value: parentCampaignName },
          { label: 'Objective', value: OBJECTIVE_LABELS[parentObjective] },
        ]}
      />

      <ReviewSection
        title="Basics"
        onEdit={() => onEditStep(1)}
        items={basicsItems}
      />

      <ReviewSection
        title="Budget &amp; Bid"
        onEdit={parentIsCbo ? undefined : () => onEditStep(2)}
        items={budgetItems}
      />

      <ReviewSection
        title="Schedule"
        onEdit={() => onEditStep(3)}
        items={[
          {
            label: 'Start date',
            value: startDate ? formatDateForDisplay(startDate) : 'Not set',
          },
          {
            label: 'End date',
            value: endExpected
              ? (endDate ? formatDateForDisplay(endDate) : 'Not set')
              : 'No end date',
          },
        ]}
      />

      <ReviewSection
        title="Targeting"
        onEdit={() => onEditStep(4)}
        items={[{ label: 'Audience', value: summariseTargeting(targeting) }]}
      />

      {requiresPromoted && (
        <ReviewSection
          title="Promoted Object"
          onEdit={() => onEditStep(5)}
          items={[
            {
              label: parentObjective === 'OUTCOME_SALES'
                ? 'Pixel + Event'
                : parentObjective === 'OUTCOME_LEADS'
                  ? 'Page'
                  : 'App',
              value: promotedObject
                ? summarisePromotedObject(promotedObject, parentObjective, pageName)
                : 'Not set',
            },
          ]}
        />
      )}

      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex">
          <svg
            className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-yellow-800">
              Before You Create
            </h4>
            <ul className="mt-1 text-xs text-yellow-700 list-disc list-inside space-y-1">
              <li>
                This will create an ad set shell. You'll need to add ads +
                creatives separately to make it serve.
              </li>
              <li>Ad set will be created with status: {STATUS_LABELS[status]}</li>
              {status === 'ACTIVE' && (
                <li className="font-semibold">
                  The ad set won't actually serve until at least one ad +
                  creative exists underneath it.
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ReviewSectionProps {
  title: string;
  onEdit?: () => void;
  items: Array<{
    label: string;
    value: string;
    detail?: string;
    highlight?: boolean;
  }>;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ title, onEdit, items }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium focus:outline-none focus:underline"
          >
            Edit
          </button>
        )}
      </div>
      <dl className="space-y-2">
        {items.map((item) => (
          <div
            key={item.label}
            className="flex flex-col sm:flex-row sm:justify-between text-sm gap-1 sm:gap-2"
          >
            <dt className="text-gray-600">{item.label}:</dt>
            <dd className="font-medium text-gray-900 sm:text-right">
              <span className={cn(item.highlight && 'text-yellow-600')}>
                {item.value}
              </span>
              {item.detail && (
                <span className="block text-xs text-gray-500 mt-0.5">
                  {item.detail}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
